import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withScoutTheme } from '../../../../ThemeContext/ThemeContext';
import { Snackbar } from '@mui/material';
import { Step1, Step2, Step3 } from './Steps';
import useStyles from '../../../../IndeedIntegration/AdEditor/useStyles';
import ExitConfirmationDialog from '../../../../../components/SeekAd/AdEditor/Components/ExitConfirmationDialog';
import StepperComponent from '../../CustomUIElements/StepperComponent';
import StepperLeftButton from '../../Buttons/StepperLeftButton';
import StepperRightButton from '../../Buttons/StepperRightButton';
import JobAdSuccessDialog from '../../CustomUIElements/JobAdSuccessDialog';
import { handleNext } from '../handleNext';
import axios from 'axios';
import _ from 'lodash';

const steps = [
  { label: 'Classify Job', value: 'job_details' },
  { label: 'Ad Description', value: 'job_description' },
  { label: 'Preview Ad', value: 'preview' }
];

function AdEditor({ jobAdParams }) {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState(updateAnswers(jobAdParams));
  const [activeError, setActiveError] = useState(null);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [editExistingJob, setEditExistingJob] = useState(false);
  const [isSnackOpen, setIsSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [checkNewListingUrl, setCheckNewListingUrl] = useState('');

  const classes = useStyles();

  function updateAnswers(params) {
    return {
      featured: params.featured || false,
      title: params.title || '',
      description: params.ad_details || '',
      classification: params.listing_classification || '',
      workType: params.work_type || '',
      workLocation: params.work_location || '',
      address: params.address || '',
      payType: params.pay_type || '',
      payRangeLow: params.salary_min || 0,
      payRangeHigh: params.salary_max || 0,
      benefits: params.benefits ? JSON.parse(params.benefits) : [],
      jobPreview: JSON.parse(params.job_preview) || {},
      externalUrl: params.external_url || '',
      externalId: params.external_id || '',
      advertisement_id: params.advertisement_id || '',
      preview_url: params.preview_url || ''
    };
  }

  async function submit(type) {
    try {
      type === 'draft' ? setSavingDraft(true) : setSaving(true);
      const token = document.querySelector('meta[name="csrf-token"]').content;
      const { data } = await axios.put(`/admin/job_ads/${jobAdParams.id}/view_jobs_ads/builder`, {
        headers: { 'Content-Type': 'application/json' },
        authenticity_token: token,
        page: activeStep + 1,
        draft: type === 'draft' ? true : false,
        job_ad: {
          title: answers.title || '',
          ad_details: answers.description || null,
          listing_classification: answers.classification || null,
          work_type: answers.workType || null,
          work_location: answers.workLocation || null,
          address: answers.address || null,
          pay_type: answers.payType || null,
          salary_min: answers.payRangeLow || 0,
          salary_max: answers.payRangeHigh || 0,
          benefits: answers.benefits || [],
          ...(activeStep === 1 && { featured: answers.featured || false }),
          ...(activeStep === 1 && answers.featured && { job_preview: answers.jobPreview }),
          external_url: answers.externalUrl || null,
          external_id: answers.externalId || null,
          advertisement_id: answers.advertisement_id || null,
          preview_url: answers.preview_url || null
        }
      });
      if (type === 'draft') {
        handleSnack('Draft saved');
        setIsExitModalOpen(false);
        window.history.back();
      } else if (type === 'submit') {
        setIsSuccessDialogOpen(true);
      } else if (type === 'next') {
        if (activeStep === 1) {
          setCheckoutUrl(data.checkout_url);
          setCheckNewListingUrl(data.check_new_listing_url);
        } else {
          const newAnswers = updateAnswers(data.job_ad);
          if (!_.isEqual(newAnswers, answers)) {
            setAnswers(newAnswers);
          }
        }
        setActiveStep(activeStep + 1);
      }
      console.log('submit', type);
    } catch (e) {
      console.log(e);
      if (type === 'draft') {
        handleSnack('Unable to save draft');
      } else if (type === 'submit') {
        handleSnack('Unable to post to ViewJobs. Please check required fields and try again.');
      } else if (type === 'next') {
        handleSnack(
          'Unable to update ViewJobs job ad. Please check required fields and try again.'
        );
      }
    } finally {
      setSaving(false);
      setSavingDraft(false);
    }
  }

  function handleSnack(message) {
    setIsSnackOpen(true);
    setSnackMessage(message);
  }

  const requiredRefs = {
    title: useRef(null),
    address: useRef(null),
    classification: useRef(null),
    workType: useRef(null),
    workLocation: useRef(null),
    payType: useRef(null),
    salary: useRef(null),
    benefits: useRef(null),
    featured: useRef(null),
    jobPreview: useRef(null)
  };

  const handleNextButton = (type) => {
    handleNext(type, checkErrors, requiredRefs, setActiveError, submit);
  };

  function checkErrors() {
    const errorChecks = {
      title: !answers.title || answers.title.length > 52,
      address: !answers.address,
      classification: !answers.classification,
      workType: !answers.workType,
      workLocation: !answers.workLocation,
      payType: !answers.payType,
      salary: !answers.payRangeHigh
    };

    const error = Object.keys(errorChecks).find((ref) => errorChecks[ref]);

    return error;
  }

  useEffect(() => {
    setActiveError(null);
  }, [answers]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeStep]);

  return (
    <div className={classes.containerOuter}>
      <div className={classes.container}>
        <StepperComponent activeStep={activeStep} steps={steps} classes={classes} />
        {activeStep === 0 && (
          <Step1
            jobAdParams={jobAdParams}
            answers={answers}
            setAnswers={setAnswers}
            requiredRefs={requiredRefs}
            activeError={activeError}
          />
        )}
        {activeStep === 1 && (
          <Step2
            jobAdParams={jobAdParams}
            answers={answers}
            setAnswers={setAnswers}
            requiredRefs={requiredRefs}
            activeError={activeError}
          />
        )}
        {activeStep === 2 && (
          <Step3
            answers={answers}
            setAnswers={setAnswers}
            checkoutUrl={checkoutUrl}
            checkNewListingUrl={checkNewListingUrl}
          />
        )}
        <div className={classes.buttonContainer}>
          <a onClick={() => setIsExitModalOpen(true)}>Save and exit</a>
          <div className={classes.buttons}>
            <StepperLeftButton
              activeStep={activeStep}
              onClick={() => setActiveStep(activeStep - 1)}
              classes={classes}
            />
            <StepperRightButton
              activeStep={activeStep}
              steps={steps}
              isSaving={saving}
              onClick={() => handleNextButton(activeStep + 1 === steps.length ? 'submit' : 'next')}
              classes={classes}
            />
          </div>
        </div>
      </div>
      <ExitConfirmationDialog
        saving={saving}
        savingDraft={savingDraft}
        setActiveStep={setActiveStep}
        isExitModalOpen={isExitModalOpen}
        setIsExitModalOpen={setIsExitModalOpen}
        editExistingJob={editExistingJob}
        submit={submit}
      />
      <JobAdSuccessDialog
        open={isSuccessDialogOpen}
        onClose={() => window.location.replace(document.referrer)}
        answers={answers}
        classes={classes}
        adType={'viewjobs'}
      />
      <Snackbar open={isSnackOpen} onClose={() => setIsSnackOpen(false)} message={snackMessage} />
    </div>
  );
}

AdEditor.propTypes = {
  jobAdParams: PropTypes.object.isRequired
};

export default withScoutTheme(AdEditor);
